'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/utils/styles/cn'
import { SOLUTIONS_GROUPS } from '@/features/landing-page/v3/solutions-menu-config'
import { LP_V3_EASE } from '@/features/landing-page/v3/landing-v3-nav-constants'

/**
 * lp-v3 Solutions mega-menu. The reveal is still the pure-CSS hover / focus-within behaviour, so every
 * group and link ships in the static HTML and stays crawlable. This thin client wrapper adds the one
 * thing CSS-hover can't: clicking an option CLOSES the menu (otherwise the panel lingers open under the
 * cursor / focused link after a client-side navigation). On click we latch the panel shut (`closed`, an
 * `!important` override that beats `group-hover` / `group-focus-within`) and reset it on mouse-leave, so
 * a fresh hover opens it again.
 */
export function SolutionsMenu() {
    const [closed, setClosed] = useState(false)

    return (
        <div className="group relative" onMouseLeave={() => setClosed(false)}>
            <button
                type="button"
                aria-haspopup="true"
                style={{ transitionTimingFunction: LP_V3_EASE }}
                className={cn(
                    'lp-nav-ink flex items-center gap-1 rounded-[8px] px-3 py-2 text-[16px] font-normal leading-[1.25] tracking-[-0.02em] text-foreground transition-colors',
                    'group-hover:bg-black/[0.05]',
                    'group-focus-within:bg-black/[0.05]'
                )}
            >
                Solutions
                <ChevronDown
                    style={{ transitionTimingFunction: LP_V3_EASE }}
                    className={cn(
                        'size-4 text-foreground/45 transition-transform duration-300 group-hover:rotate-180 group-focus-within:rotate-180',
                        // Keep the chevron pointing down while the menu is latched shut after a click.
                        closed && 'group-hover:!rotate-0 group-focus-within:!rotate-0'
                    )}
                />
            </button>

            {/* Panel: always in the DOM (crawlable); the `pt-3` is a hover bridge to the trigger. */}
            <div
                className={cn(
                    'absolute left-1/2 top-full origin-top -translate-x-1/2 pt-3',
                    'translate-y-1 scale-[0.98] opacity-0 pointer-events-none',
                    'transition-[opacity,transform,translate,scale] duration-200 ease-[cubic-bezier(0.4,0,1,1)] motion-reduce:transition-none',
                    'group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:scale-100 group-hover:opacity-100',
                    'group-hover:duration-[320ms] group-hover:ease-[cubic-bezier(0.16,1,0.3,1)]',
                    'group-focus-within:pointer-events-auto group-focus-within:translate-y-0 group-focus-within:scale-100 group-focus-within:opacity-100',
                    'group-focus-within:duration-[320ms] group-focus-within:ease-[cubic-bezier(0.16,1,0.3,1)]',
                    // Clicking an option latches the menu shut until the pointer leaves the group.
                    closed && '!pointer-events-none !translate-y-1 !scale-[0.98] !opacity-0'
                )}
            >
                <div className="flex gap-6 rounded-2xl bg-white p-5 shadow-[0_16px_48px_rgba(0,0,0,0.16)] ring-1 ring-black/5">
                    {SOLUTIONS_GROUPS.map((group) => (
                        <div key={group.heading} className="min-w-[168px]">
                            <div className="px-2 pb-2 text-[11px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
                                {group.heading}
                            </div>
                            <div className="flex flex-col">
                                {group.items.map((item) => (
                                    <Link
                                        key={item.label}
                                        href={item.href}
                                        prefetch={false}
                                        onClick={(event) => {
                                            setClosed(true)
                                            event.currentTarget.blur()
                                        }}
                                        className="flex items-center gap-2.5 rounded-[8px] px-2 py-2 text-[14px] text-foreground/80 transition-colors hover:bg-black/[0.04] hover:text-foreground"
                                    >
                                        {item.iconSrc && (
                                            <Image
                                                src={item.iconSrc}
                                                alt=""
                                                width={18}
                                                height={18}
                                                className="size-[18px] shrink-0"
                                            />
                                        )}
                                        {item.label}
                                    </Link>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
